import React, { useMemo, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormTabs, tabHasFieldErrors } from "../FormTabs";
import { FormHelpCallout } from "../FormHelpCallout";
import type {
  CreateCustomerProjectionData,
  SubscriptionTier,
  CustomerCapacityGroup,
} from "../../../types/startup.types";
import {
  calculateEquilibriumCustomers,
  getRevenuePlanLabel,
} from "../../../utils/chart/customer-projection.utils";

export interface CustomerProjectionFormProps {
  onSubmit: (data: CreateCustomerProjectionData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  initialData?: Partial<CreateCustomerProjectionData>;
  isLoading?: boolean;
  isEditing?: boolean;
  subscriptionTiers?: SubscriptionTier[];
  capacityGroups?: CustomerCapacityGroup[];
}

const PROJECTION_TYPES = [
  {
    value: "fixed_monthly",
    label: "Fixed monthly acquisition",
    description: "Add a set number of new customers every month (best for cold start).",
  },
  {
    value: "growth_rate",
    label: "Growth + acquisition",
    description:
      "Grow existing customers by % each month, plus optional fixed new customers per month.",
  },
  {
    value: "custom_timeline",
    label: "Custom timeline",
    description: "Define expected customer counts for specific months.",
  },
] as const;

const ACQUISITION_CHANNELS = [
  "paid_search",
  "social_media",
  "content_marketing",
  "referral",
  "organic",
  "email_marketing",
  "partnerships",
  "events",
  "other",
] as const;

type TabId = "basics" | "growth" | "costs" | "capacity";

const TAB_FIELDS: Record<TabId, (keyof FormData)[]> = {
  basics: ["name", "description", "subscriptionTierId", "startDate", "endDate"],
  growth: [
    "projectionType",
    "initialCustomers",
    "monthlyAcquisitionTarget",
    "monthlyGrowthRate",
    "churnRateMonthly",
  ],
  costs: [
    "acquisitionCostPerCustomer",
    "monthlyServicingCostPerCustomer",
    "acquisitionChannel",
  ],
  capacity: ["maxCustomers", "maxMonthlyAcquisitions", "capacityGroupId"],
};

interface FormData {
  name: string;
  description?: string;
  subscriptionTierId: string;
  projectionType: "fixed_monthly" | "growth_rate" | "custom_timeline";
  monthlyAcquisitionTarget?: number;
  initialCustomers?: number;
  monthlyGrowthRate?: number;
  churnRateMonthly: number;
  acquisitionCostPerCustomer: number;
  monthlyServicingCostPerCustomer?: number;
  maxCustomers?: number;
  maxMonthlyAcquisitions?: number;
  capacityGroupId?: string;
  startDate: string;
  endDate?: string;
  acquisitionChannel?: string;
}

function firstTabWithErrors(errors: FieldErrors<FormData>): TabId {
  const order: TabId[] = ["basics", "growth", "costs", "capacity"];
  return (
    order.find((tabId) => tabHasFieldErrors(errors, TAB_FIELDS[tabId])) ?? "basics"
  );
}

export const CustomerProjectionForm: React.FC<CustomerProjectionFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  initialData,
  isLoading = false,
  isEditing = false,
  subscriptionTiers = [],
  capacityGroups = [],
}) => {
  const [activeTab, setActiveTab] = useState<TabId>("basics");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      subscriptionTierId: initialData?.subscriptionTierId || "",
      projectionType: initialData?.projectionType || "fixed_monthly",
      monthlyAcquisitionTarget: initialData?.monthlyAcquisitionTarget || 10,
      initialCustomers: initialData?.initialCustomers || 0,
      monthlyGrowthRate: initialData?.monthlyGrowthRate
        ? initialData.monthlyGrowthRate * 100
        : 5,
      churnRateMonthly:
        initialData?.churnRateMonthly !== undefined
          ? initialData.churnRateMonthly * 100
          : 2,
      acquisitionCostPerCustomer: initialData?.acquisitionCostPerCustomer || 100,
      monthlyServicingCostPerCustomer:
        initialData?.monthlyServicingCostPerCustomer || 0,
      maxCustomers: initialData?.maxCustomers,
      maxMonthlyAcquisitions: initialData?.maxMonthlyAcquisitions,
      capacityGroupId: initialData?.capacityGroupId || "",
      startDate: initialData?.startDate
        ? new Date(initialData.startDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      endDate: initialData?.endDate
        ? new Date(initialData.endDate).toISOString().split("T")[0]
        : "",
      acquisitionChannel: initialData?.acquisitionChannel || "",
    },
  });

  const projectionType = watch("projectionType");
  const watchedValues = watch();

  const tabs = useMemo(
    () => [
      {
        id: "basics" as const,
        label: "Basics",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.basics),
      },
      {
        id: "growth" as const,
        label: "Growth",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.growth),
      },
      {
        id: "costs" as const,
        label: "Costs",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.costs),
      },
      {
        id: "capacity" as const,
        label: "Capacity",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.capacity),
      },
    ],
    [errors],
  );

  const capacityInsights = useMemo(() => {
    const equilibrium = calculateEquilibriumCustomers({
      projectionType: watchedValues.projectionType,
      subscriptionTierId: watchedValues.subscriptionTierId || "tier",
      startDate: watchedValues.startDate,
      churnRateMonthly: Number(watchedValues.churnRateMonthly || 0) / 100,
      monthlyAcquisitionTarget: Number(watchedValues.monthlyAcquisitionTarget || 0),
      monthlyGrowthRate:
        watchedValues.projectionType === "growth_rate"
          ? Number(watchedValues.monthlyGrowthRate || 0) / 100
          : undefined,
    });

    const insights: string[] = [];
    if (equilibrium !== null) {
      insights.push(
        `Uncapped steady-state: ~${Math.round(equilibrium).toLocaleString()} customers (acquisitions ÷ churn).`,
      );
      if (
        watchedValues.maxCustomers &&
        equilibrium > Number(watchedValues.maxCustomers)
      ) {
        insights.push(
          `Your cap (${Number(watchedValues.maxCustomers).toLocaleString()}) is below steady-state — growth will flatten at the cap.`,
        );
      }
    } else if ((watchedValues.churnRateMonthly || 0) <= 0) {
      insights.push(
        "With 0% churn and growth, customer count can grow without a cap — set max customers for realism.",
      );
    }

    if (!watchedValues.maxCustomers) {
      insights.push(
        "Tip: set a max customers cap to model office or staffing limits.",
      );
    }

    return insights;
  }, [watchedValues]);

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      name: data.name,
      description: data.description,
      subscriptionTierId: data.subscriptionTierId,
      startupId: "",
      projectionType: data.projectionType,
      monthlyAcquisitionTarget:
        data.projectionType === "fixed_monthly" ||
        data.projectionType === "growth_rate"
          ? Number(data.monthlyAcquisitionTarget) || undefined
          : undefined,
      initialCustomers: data.initialCustomers
        ? Number(data.initialCustomers)
        : undefined,
      monthlyGrowthRate:
        data.projectionType === "growth_rate"
          ? Number(data.monthlyGrowthRate) / 100
          : undefined,
      churnRateMonthly: Number(data.churnRateMonthly) / 100,
      acquisitionCostPerCustomer: Number(data.acquisitionCostPerCustomer),
      monthlyServicingCostPerCustomer: data.monthlyServicingCostPerCustomer
        ? Number(data.monthlyServicingCostPerCustomer)
        : undefined,
      maxCustomers: data.maxCustomers ? Number(data.maxCustomers) : undefined,
      maxMonthlyAcquisitions: data.maxMonthlyAcquisitions
        ? Number(data.maxMonthlyAcquisitions)
        : undefined,
      capacityGroupId: data.capacityGroupId || undefined,
      startDate: new Date(data.startDate),
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      acquisitionChannel: data.acquisitionChannel || undefined,
    });
  };

  const handleValidationError = (formErrors: FieldErrors<FormData>) => {
    setActiveTab(firstTabWithErrors(formErrors));
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit, handleValidationError)}
      className="space-y-4"
    >
      <FormTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as TabId)}
      />

      <div className="min-h-[280px] pt-1">
        {activeTab === "basics" ? (
          <div className="space-y-4">
            <FormHelpCallout
              variant="tip"
              title="Revenue plan vs capacity"
              learnMoreHref="/help/customer-projections"
            >
              Pick a <strong className="text-white/75">revenue plan</strong> for
              pricing only. Customer counts and location limits are set on other
              tabs — capacity groups are linked on the Capacity tab, not here.
            </FormHelpCallout>

            <FormField label="Projection name" error={errors.name?.message}>
              <Input
                {...register("name", { required: "Projection name is required" })}
                placeholder="e.g., Dental patients, B2B accounts"
              />
            </FormField>

            <FormField label="Description (optional)" error={errors.description?.message}>
              <Input
                {...register("description")}
                placeholder="Brief description of this customer segment"
              />
            </FormField>

            <FormField
              label="Revenue plan"
              error={errors.subscriptionTierId?.message}
              helperText="How each customer is billed — subscription, per visit, or hybrid. Does not limit how many customers you can have."
            >
              <select
                {...register("subscriptionTierId", {
                  required: "Revenue plan is required",
                })}
                className="sh-select"
              >
                <option value="">Select a revenue plan...</option>
                {subscriptionTiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {getRevenuePlanLabel(tier)}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Start date" error={errors.startDate?.message}>
                <Input
                  type="date"
                  {...register("startDate", { required: "Start date is required" })}
                />
              </FormField>
              <FormField label="End date (optional)" error={errors.endDate?.message}>
                <Input type="date" {...register("endDate")} />
              </FormField>
            </div>
          </div>
        ) : null}

        {activeTab === "growth" ? (
          <div className="space-y-4">
            <FormField label="Projection type" error={errors.projectionType?.message}>
              <select {...register("projectionType")} className="sh-select">
                {PROJECTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-white/45">
                {
                  PROJECTION_TYPES.find((type) => type.value === projectionType)
                    ?.description
                }
              </p>
            </FormField>

            {projectionType === "fixed_monthly" ? (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Initial customers"
                  error={errors.initialCustomers?.message}
                >
                  <Input
                    type="number"
                    {...register("initialCustomers", {
                      min: { value: 0, message: "Must be 0 or greater" },
                    })}
                  />
                </FormField>
                <FormField
                  label="Monthly acquisition target"
                  error={errors.monthlyAcquisitionTarget?.message}
                >
                  <Input
                    type="number"
                    {...register("monthlyAcquisitionTarget", {
                      required: "Monthly acquisition target is required",
                      min: { value: 0, message: "Must be 0 or greater" },
                    })}
                  />
                </FormField>
              </div>
            ) : null}

            {projectionType === "growth_rate" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Starting customers"
                    error={errors.initialCustomers?.message}
                  >
                    <Input
                      type="number"
                      {...register("initialCustomers", {
                        min: { value: 0, message: "Must be 0 or greater" },
                      })}
                    />
                  </FormField>
                  <FormField
                    label="Monthly growth rate (%)"
                    error={errors.monthlyGrowthRate?.message}
                  >
                    <Input
                      type="number"
                      step="0.1"
                      {...register("monthlyGrowthRate", {
                        required: "Growth rate is required",
                        min: { value: 0, message: "Must be 0 or greater" },
                      })}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Fixed new customers / month (optional)"
                  error={errors.monthlyAcquisitionTarget?.message}
                >
                  <Input
                    type="number"
                    {...register("monthlyAcquisitionTarget", {
                      min: { value: 0, message: "Must be 0 or greater" },
                    })}
                  />
                </FormField>
              </div>
            ) : null}

            {projectionType === "custom_timeline" ? (
              <FormField
                label="Initial customers"
                error={errors.initialCustomers?.message}
              >
                <Input
                  type="number"
                  {...register("initialCustomers", {
                    min: { value: 0, message: "Must be 0 or greater" },
                  })}
                />
              </FormField>
            ) : null}

            <FormField
              label="Monthly churn rate (%)"
              error={errors.churnRateMonthly?.message}
            >
              <Input
                type="number"
                step="0.1"
                {...register("churnRateMonthly", {
                  required: "Churn rate is required",
                  min: { value: 0, message: "Must be 0 or greater" },
                  max: { value: 100, message: "Must be 100 or less" },
                })}
              />
            </FormField>
          </div>
        ) : null}

        {activeTab === "costs" ? (
          <div className="space-y-4">
            <FormField
              label="Acquisition cost / customer ($)"
              error={errors.acquisitionCostPerCustomer?.message}
            >
              <Input
                type="number"
                step="0.01"
                {...register("acquisitionCostPerCustomer", {
                  required: "Acquisition cost is required",
                  min: { value: 0, message: "Must be 0 or greater" },
                })}
              />
            </FormField>

            <FormField
              label="Servicing cost / active customer / month ($)"
              error={errors.monthlyServicingCostPerCustomer?.message}
            >
              <Input
                type="number"
                step="0.01"
                {...register("monthlyServicingCostPerCustomer", {
                  min: { value: 0, message: "Must be 0 or greater" },
                })}
                placeholder="e.g., reminders, records, support per customer"
              />
            </FormField>

            <FormField
              label="Acquisition channel (optional)"
              error={errors.acquisitionChannel?.message}
            >
              <select {...register("acquisitionChannel")} className="sh-select">
                <option value="">Select channel...</option>
                {ACQUISITION_CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {channel
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        ) : null}

        {activeTab === "capacity" ? (
          <div className="space-y-4">
            <FormHelpCallout
              learnMoreHref="/help/capacity-groups"
              learnMoreLabel="Full guide: capacity groups"
            >
              A <strong className="text-white/75">shared capacity group</strong>{" "}
              limits the <em>combined</em> customers across every projection
              using it (e.g. one office with 200 chairs). Use{" "}
              <strong className="text-white/75">Max customers</strong> below to
              cap only this projection when you do not need sharing.
            </FormHelpCallout>
            <p className="text-xs text-white/45">
              Model staffing, office space, or shared location limits that constrain
              how many customers you can serve.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Max customers (cap)"
                error={errors.maxCustomers?.message}
                helperText="Limit for this projection only."
              >
                <Input
                  type="number"
                  {...register("maxCustomers", {
                    min: { value: 1, message: "Must be at least 1" },
                  })}
                  placeholder="e.g., 2400 patients"
                />
              </FormField>
              <FormField
                label="Max new customers / month"
                error={errors.maxMonthlyAcquisitions?.message}
                helperText="Onboarding speed cap for this projection."
              >
                <Input
                  type="number"
                  {...register("maxMonthlyAcquisitions", {
                    min: { value: 1, message: "Must be at least 1" },
                  })}
                  placeholder="Onboarding limit"
                />
              </FormField>
            </div>
            <FormField
              label="Shared capacity group (optional)"
              error={errors.capacityGroupId?.message}
              helperText="Link multiple projections to the same office or resource pool. Their active customers sum toward the group max."
            >
              <select {...register("capacityGroupId")} className="sh-select">
                <option value="">No shared group</option>
                {capacityGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} (max {group.maxCustomers.toLocaleString()})
                  </option>
                ))}
              </select>
            </FormField>
            {capacityInsights.length > 0 ? (
              <ul className="space-y-1 rounded-[14px] border border-warn/20 bg-warn/5 p-3 text-xs text-warn/90">
                {capacityInsights.map((insight) => (
                  <li key={insight}>• {insight}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex justify-between border-t border-white/10 pt-4">
        <div>
          {isEditing && onDelete ? (
            <Button
              type="button"
              variant="danger"
              size="md"
              onClick={onDelete}
              disabled={isLoading}
            >
              Delete
            </Button>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={isLoading}>
            {isLoading ? "Saving..." : isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </form>
  );
};
